/**
 * Client-side Validation
 *
 * @package DesignSchool\Forms
 */

import { PHONE_INPUT_SELECTOR } from './phone-mask';
import { isValidPhoneInput } from './phone-validation';
import { isValidPhoneE164, normalizePhone } from './phone-utils';

declare var jQuery: any;

function getPhoneMaskApi(): {
    getTelValue?: (el: HTMLInputElement) => string | Promise<string>;
    getTelDigitCount?: (el: HTMLInputElement) => number;
    syncTelInputsToE164?: ($form: any) => void | Promise<void>;
} | null {
    return (typeof window !== 'undefined' && (window as any).dsFormsPhoneMask) || null;
}

function resolveFieldKey($field: any): string {
    const name = ($field.attr('name') || '').toString().trim();
    if (name) {
        return name;
    }
    const id = ($field.attr('id') || '').toString().trim();
    return id;
}

export class FormValidation {
    private $: any;

    constructor() {
        this.$ = jQuery;
    }

    public validatePhone(phone: string, telInput?: HTMLInputElement): boolean {
        if (!phone || typeof phone !== 'string') {
            return true;
        }

        if (telInput) {
            return isValidPhoneInput(telInput);
        }

        return isValidPhoneE164(phone);
    }

    public validateEmail(email: string): boolean {
        const re = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        return re.test(email);
    }

    public validateRequired(value: string): boolean {
        return !!(value && value.trim() !== '');
    }

    private isFieldRequired($el: any): boolean {
        return (
            $el.attr('required') !== undefined ||
            $el.hasClass('ds-form__input--required') ||
            $el.closest('.ds-form__field--required').length > 0
        );
    }

    private async validatePhoneFields($form: any, errors: Record<string, string>): Promise<void> {
        const phoneMaskApi = getPhoneMaskApi();
        const phoneFields: HTMLInputElement[] = [];

        $form.find(PHONE_INPUT_SELECTOR).each((_: number, element: HTMLElement) => {
            if (element instanceof HTMLInputElement) {
                phoneFields.push(element);
            }
        });

        for (const element of phoneFields) {
            const $field = this.$(element);
            const fieldKey = resolveFieldKey($field);
            if (!fieldKey) {
                continue;
            }

            const digitCount =
                phoneMaskApi && typeof phoneMaskApi.getTelDigitCount === 'function'
                    ? phoneMaskApi.getTelDigitCount(element)
                    : (element.value || '').replace(/\D/g, '').length;

            const isRequired = this.isFieldRequired($field);

            if (isRequired && digitCount < 10) {
                errors[fieldKey] = 'Поле обязательно для заполнения';
                this.setFieldError($form, $field, errors[fieldKey]);
                continue;
            }

            if (digitCount >= 10 && !isValidPhoneInput(element)) {
                errors[fieldKey] = 'Неверный формат телефона';
                this.setFieldError($form, $field, errors[fieldKey]);
            }
        }
    }

    public async validateForm($form: any): Promise<{ valid: boolean; errors: Record<string, string> }> {
        const errors: Record<string, string> = {};

        $form.find('.ds-form__error').text('');
        $form.find('.form__error, .consult-field-error, .ds-field-hint').remove();
        $form.find('.ds-form__field--error, .ds-form__checkbox--error, .checkbox--error').removeClass(
            'ds-form__field--error ds-form__checkbox--error checkbox--error'
        );
        $form.find('.ds-form__input--error, .input--error').removeClass('ds-form__input--error input--error');

        await this.validatePhoneFields($form, errors);

        $form.find('input[type="text"], input[type="email"]')
            .filter((_: number, el: HTMLElement) => this.isFieldRequired(this.$(el)))
            .each((_: number, element: HTMLElement) => {
                const $field = this.$(element);
                const fieldKey = resolveFieldKey($field);
                if (!fieldKey) {
                    return;
                }
                const value = ($field.val() || '').toString().trim();
                if (!this.validateRequired(value)) {
                    errors[fieldKey] = 'Поле обязательно для заполнения';
                    this.setFieldError($form, $field, errors[fieldKey]);
                }
            });

        $form.find('input[type="email"][name]').each((_: number, element: HTMLElement) => {
            const $field = this.$(element);
            const fieldKey = resolveFieldKey($field);
            if (!fieldKey) {
                return;
            }
            const value = ($field.val() || '').toString().trim();
            if (value && !this.validateEmail(value)) {
                errors[fieldKey] = 'Неверный формат email';
                this.setFieldError($form, $field, errors[fieldKey]);
            }
        });

        $form.find('input[type="checkbox"][name]:not(.ds-form__honeypot)').each((_: number, element: HTMLElement) => {
            const $checkbox = this.$(element);
            const fieldKey = resolveFieldKey($checkbox);
            if (!fieldKey) {
                return;
            }

            const mustValidate =
                $checkbox.attr('required') !== undefined ||
                fieldKey === 'privacy' ||
                fieldKey === 'contract-offer';

            if (!mustValidate) {
                return;
            }

            if (!$checkbox.is(':checked')) {
                errors[fieldKey] = 'Необходимо подтвердить согласие на обработку персональных данных';
                this.setFieldError($form, $checkbox, errors[fieldKey]);
            }
        });

        return {
            valid: Object.keys(errors).length === 0,
            errors
        };
    }

    private setFieldError($form: any, $fieldEl: any, message: string): void {
        const $wrapper = $fieldEl.closest('.ds-form__field, .ds-form__checkbox, .checkbox, .consult__field, .field');
        if (!$wrapper.length) {
            return;
        }

        $wrapper.find('.form__error, .consult-field-error, .ds-field-hint').remove();

        const isCheckbox = $wrapper.hasClass('ds-form__checkbox') || $wrapper.hasClass('checkbox');
        if (isCheckbox) {
            $wrapper.addClass('ds-form__checkbox--error checkbox--error');
            $fieldEl.addClass('ds-form__input--error');
        } else {
            $wrapper.addClass('ds-form__field--error');
            const $input = $fieldEl.is('input, select, textarea')
                ? $fieldEl
                : $wrapper.find('.ds-form__input, .ds-form__select, .ds-form__textarea, .input').first();
            if ($input.length) {
                $input.addClass('ds-form__input--error input--error');
            }
        }

        // плагин: один span.ds-form__error; consult/legacy: div.ds-field-hint
        const $errorSlot = $wrapper.children('.ds-form__error').first();
        if ($errorSlot.length) {
            $errorSlot.text(message || '');
            return;
        }

        if (message) {
            $wrapper.append(
                this.$('<div class="form__error consult-field-error ds-field-hint" role="alert">').text(message)
            );
        }
    }

    public showError(fieldName: string, message: string, $form?: any): void {
        if (!fieldName || fieldName === 'undefined') {
            return;
        }
        const $scope = $form && $form.length ? $form : this.$(document);
        const $field = $scope.find(`[name="${fieldName}"]`).first();
        if ($field.length) {
            this.setFieldError($scope, $field, message);
        }
    }

    public highlightFields($form: any, fieldNames: string[]): void {
        fieldNames
            .filter((name) => name && name !== 'undefined')
            .forEach((name: string) => {
                const $field = $form.find(`[name="${name}"]`).first();
                if ($field.length) {
                    const $wrapper = $field.closest('.ds-form__field, .ds-form__checkbox, .consult__field');
                    $wrapper.addClass($wrapper.hasClass('ds-form__checkbox') ? 'ds-form__checkbox--error' : 'ds-form__field--error');
                }
            });
    }

    public clearHighlights($form?: any): void {
        const $scope = $form && $form.length ? $form : this.$(document);
        $scope.find('.ds-form__field--error, .ds-form__checkbox--error, .checkbox--error').removeClass(
            'ds-form__field--error ds-form__checkbox--error checkbox--error'
        );
        $scope.find('.ds-form__input--error, .input--error').removeClass('ds-form__input--error input--error');
        $scope.find('.form__error, .consult-field-error, .ds-field-hint').remove();
    }

    public getFieldLabels($form: any, fieldNames: string[]): string[] {
        const labels: string[] = [];
        const knownNames: Record<string, string> = {
            form2_name: 'Имя',
            form2_surname: 'Фамилия',
            form2_email: 'Email',
            form2_phone: 'Телефон',
            name: 'Имя',
            email: 'Email',
            phone: 'Телефон',
            privacy: 'Согласие с политикой конфиденциальности',
            'contract-offer': 'Согласие с договором-офертой',
            instruction: 'Инструкция'
        };

        fieldNames
            .filter((name) => name && name !== 'undefined')
            .forEach((name: string) => {
                if (knownNames[name]) {
                    labels.push(knownNames[name]);
                    return;
                }
                const $field = $form.find(`[name="${name}"]`).first();
                if ($field.length) {
                    const $label = $field.closest('.ds-form__field, .consult__field').find('.ds-form__label, label').first();
                    const labelText = $label.length ? $label.text().replace(/\s*\*\s*$/, '').trim() : '';
                    labels.push(labelText || name);
                } else {
                    labels.push(name);
                }
            });
        return labels;
    }

    public clearErrors(): void {
        this.$('.ds-form__error').text('');
    }
}

export const formValidation = new FormValidation();
